import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { ClientAgent, ClientInfo, ClientContextType } from '../types/business';

// Create context with default undefined value
export const ClientContext = createContext<ClientContextType | undefined>(undefined);

export function ClientProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  
  // Client state
  const [client, setClient] = useState<ClientInfo | null>(null);
  const [agent, setAgent] = useState<ClientAgent | null>(null);
  const [hasAgent, setHasAgent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refreshClientInfo = async () => {
    // Only fetch if user is authenticated
    if (!isAuthenticated || !user) {
      setClient(null);
      setAgent(null);
      setHasAgent(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/client', {
        method: 'GET',
        credentials: 'same-origin'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Update: Use data.client instead of data.clientInfo
        if (data.client) {
          setClient({
            name: data.client.name,
            email: data.client.email,
            picture: data.client.picture,
            joinedDate: data.client.joinedDate
          });

          // Update: Use data.agent and data.hasAgent
          if (data.hasAgent && data.agent) {
            setAgent({
              name: data.agent.name,
              email: data.agent.email,
              phone: data.agent.phone,
              profile: data.agent.profile,
              businessName: data.agent.businessName
            });
            setHasAgent(true);
          } else {
            setAgent(null);
            setHasAgent(false);
          }
        } else {
          setClient(null);
          setAgent(null);
          setHasAgent(false);
        }
      } else {
        setError(data.error || 'Failed to fetch client info');
        setClient(null);
        setAgent(null);
        setHasAgent(false);
      }
    } catch (error) {
      console.error('Error fetching client info:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
      setClient(null);
      setAgent(null);
      setHasAgent(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch client info when user authentication status changes
  useEffect(() => {
    refreshClientInfo();
  }, [isAuthenticated, user?.id]);

  return (
    <ClientContext.Provider
      value={{
        client,
        agent,
        hasAgent,
        isLoading,
        error,
        refreshClientInfo
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

export const useClient = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};